import { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Label } from "./ui/label";

const Opt = ({ on, onClick, icon: I, children, testid, disabled }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2 text-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${on ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-400 hover:bg-zinc-100"}`}>
    <I className="h-4 w-4" /> {children}
  </button>
);

export const PvpScopeToggle = ({ pvp, disabled, onPvp }) => {
  const [units, setUnits] = useState([]);
  const team = pvp.scope === "team";
  const rank = (k) => (/:S5$/.test(k) ? 3 : /:S3$/.test(k) ? 2 : 0);
  const preferred = [...units].sort((a, b) => rank(a.key) - rank(b.key))[0]?.key;

  useEffect(() => {
    api.get(`/pvps/${pvp.id}/team-options`).then(({ data }) => setUnits(data));
  }, [pvp.id]);

  const save = async (scope, unit_key = "") => {
    try {
      const { data } = await api.put(`/pvps/${pvp.id}/scope`, { scope, unit_key });
      onPvp(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    }
  };

  return (
    <div className="border border-zinc-900 p-4 mb-8 space-y-3" data-testid="pvp-scope">
      <Label className="text-sm font-medium block">Gaat het om een persoonlijke PVP of een team-PVP?</Label>
      <div className="flex flex-wrap gap-2">
        <Opt on={!team} disabled={disabled} onClick={() => save("personal")} icon={User} testid="pvp-scope-personal">Persoonlijk</Opt>
        <Opt on={team} disabled={disabled || !units.length} onClick={() => save("team", preferred)} icon={Users} testid="pvp-scope-team">Team</Opt>
      </div>
      {!units.length && <p className="text-xs text-zinc-500">{pvp.employee_name} zit nog niet in een systeem of afdeling; een team-PVP is daarom niet mogelijk.</p>}
      {team && (
        <div className="space-y-3 pt-1">
          {units.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="pvp-scope-units">
              {units.map((u) => (
                <button key={u.key} type="button" disabled={disabled} onClick={() => save("team", u.key)} data-testid={`pvp-scope-unit-${u.key}`}
                  className={`px-3 py-1 text-xs border transition-colors disabled:cursor-not-allowed ${pvp.team_unit_key === u.key ? "border-zinc-900 font-semibold" : "border-zinc-300 text-zinc-500 hover:border-zinc-900"}`}>{u.label}</button>
              ))}
            </div>
          )}
          <p className="text-sm" data-testid="pvp-scope-unit-label">Gekoppeld aan iedereen in <span className="font-semibold">{pvp.team_unit_label}</span> met de vaardigheid "{pvp.behavior_text}":</p>
          <ul className="flex flex-wrap gap-2" data-testid="pvp-team-members">
            {(pvp.team_members || []).map((m) => (
              <li key={m.id} className={`text-xs px-2 py-1 border ${m.own_pvp ? "border-dashed border-zinc-300 text-zinc-400" : "border-zinc-900"}`}
                title={m.own_pvp ? "Heeft een eigen PVP voor deze vaardigheid; die blijft voor deze medewerker gelden" : ""} data-testid={`pvp-team-member-${m.id}`}>
                {m.name}{m.own_pvp && " · eigen PVP"}
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500">Wie later met deze vaardigheid in dit systeem of deze afdeling komt, krijgt deze PVP automatisch ook.</p>
        </div>
      )}
    </div>
  );
};
